import React, { useState, useContext } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import AppContext from '../../contexts/AppContext';
import { registerUser } from '../../services/authService';
import { verifyRecaptcha } from '../../services/firestoreService';
import { getRecaptchaToken } from '../../utils/recaptcha';
import SecurityUtils from '../../utils/security';
import GooglePlacesAutocomplete from '../common/GooglePlacesAutocomplete';
import DateInput from '../common/DateInput';

const RegistrationScreen = ({ onBackToLogin }) => {
  const { users, showAlert, effectiveTheme } = useContext(AppContext);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegConfirmPassword, setShowRegConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    handle: '',
    email: '',
    password: '',
    confirmPassword: '',
    birthDate: '',
    gender: '',
    bio: '',
    skillLevel: 'Beginner',
    handicap: '',
    location: ''
  });
  const [selectedLocationData, setSelectedLocationData] = useState(null);
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Handle validation
    if (!formData.handle.trim()) {
      newErrors.handle = 'Handle is required';
    } else if (users.some(u => u.handle.toLowerCase() === formData.handle.toLowerCase())) {
      newErrors.handle = 'This handle is already taken';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    } else if (users.some(u => u.email.toLowerCase() === formData.email.toLowerCase())) {
      newErrors.email = 'This email is already registered';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one lowercase letter';
    } else if (!/(?=.*[A-Z])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter';
    } else if (!/(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
    } else if (!/(?=.*[@$!%*?&#])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one special character (@$!%*?&#)';
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Birth date validation
    if (!formData.birthDate) {
      newErrors.birthDate = 'Birth date is required';
    } else {
      const birthDate = new Date(formData.birthDate);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age;

      if (actualAge < 13) {
        newErrors.birthDate = 'You must be at least 13 years old';
      }
    }

    // Bio is optional - no validation

    // Handicap is optional - no validation

    // Location validation
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async () => {
    if (!validateForm()) return;
    setRegisterLoading(true);
    try {
      const token = await getRecaptchaToken('signup');
      await verifyRecaptcha(token, 'signup');
      const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`;
      const profileData = {
        name: SecurityUtils.sanitizeText(fullName),
        firstName: SecurityUtils.sanitizeText(formData.firstName.trim()),
        lastName: SecurityUtils.sanitizeText(formData.lastName.trim()),
        handle: SecurityUtils.sanitizeText(formData.handle.trim().toLowerCase()),
        skillLevel: formData.skillLevel,
        handicap: formData.handicap ? parseInt(formData.handicap) : 0,
        location: {
          address: SecurityUtils.sanitizeText(formData.location.trim()),
          lat: selectedLocationData?.lat || null,
          lon: selectedLocationData?.lon || null,
          zipCode: selectedLocationData?.zipCode || '',
        },
        bio: SecurityUtils.sanitizeText(formData.bio.trim() || ''),
        birthDate: formData.birthDate,
        gender: formData.gender || '',
        profilePicture: `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&background=16a34a&color=fff&size=128`,
      };
      await registerUser(formData.email.trim().toLowerCase(), formData.password, profileData);
      // Auth state change listener will handle setting currentUser and loading data
    } catch (err) {
      if (err.code === 'functions/permission-denied') {
        showAlert('Your request was flagged as suspicious. Please try again.', 'Error');
      } else if (err.code === 'auth/email-already-in-use') {
        showAlert('This email is already registered.', 'Error');
      } else if (err.code === 'auth/weak-password') {
        showAlert('Password is too weak. Must be at least 6 characters.', 'Error');
      } else {
        showAlert('Registration failed: ' + err.message, 'Error');
      }
    } finally {
      setRegisterLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-gray-900 to-black overflow-y-auto">
      <div className="max-w-2xl w-full bg-black rounded-lg shadow-xl border border-gray-600 p-8 my-8 mx-auto" style={{ marginBottom: '2rem' }}>
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <img src="/logos/logo-transparent-smll.png" alt="" className="h-12 w-auto object-contain" />
            <img
              src={effectiveTheme === 'dark' ? '/logos/logo-loft-dark-theme-582x243.png' : '/logos/logo-loft-light-theme-582x243.png'}
              alt="Loft"
              className="h-12 w-auto object-contain"
            />
          </div>
          <p className="text-white mt-2">Create your golfer profile</p>
        </div>

        <div className="space-y-4 px-2">
          {/* Name */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-white mb-1">First Name *</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                placeholder="John"
              />
              {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-white mb-1">Last Name *</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                placeholder="Doe"
              />
              {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
            </div>
          </div>

          {/* Handle */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Username/Handle *</label>
            <input
              type="text"
              value={formData.handle}
              onChange={(e) => handleChange('handle', e.target.value)}
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
              placeholder="johndoe"
            />
            {errors.handle && <p className="text-red-500 text-xs mt-1">{errors.handle}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
              placeholder="john@example.com"
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Password *</label>
            <div className="relative">
              <input
                type={showRegPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                className="w-full px-2 py-1 pr-10 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                placeholder="••••••••"
              />
              <button type="button" onClick={() => setShowRegPassword(!showRegPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                {showRegPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-white mt-1">
              Min 8 characters, include uppercase, lowercase, number, and special character (@$!%*?&#)
            </p>
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Confirm Password *</label>
            <div className="relative">
              <input
                type={showRegConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                className="w-full px-2 py-1 pr-10 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                placeholder="••••••••"
              />
              <button type="button" onClick={() => setShowRegConfirmPassword(!showRegConfirmPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                {showRegConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
          </div>

          {/* Birth Date */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Birth Date *</label>
            <DateInput
              value={formData.birthDate}
              onChange={(v) => handleChange('birthDate', v)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
            />
            {errors.birthDate && <p className="text-red-500 text-xs mt-1">{errors.birthDate}</p>}
          </div>

          {/* Gender */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Gender</label>
            <select
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
            >
              <option value="">Prefer not to say</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Non-binary">Non-binary</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Bio */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Bio</label>
            <textarea
              value={formData.bio}
              onChange={(e) => handleChange('bio', e.target.value)}
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black resize-none"
              rows="3"
              placeholder="Tell us about yourself..."
            />
            {errors.bio && <p className="text-red-500 text-xs mt-1">{errors.bio}</p>}
          </div>

          {/* Skill Level */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Skill Level *</label>
            <select
              value={formData.skillLevel}
              onChange={(e) => handleChange('skillLevel', e.target.value)}
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
            >
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
              <option value="Expert">Expert</option>
            </select>
          </div>

          {/* Handicap */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Handicap</label>
            <input
              type="number"
              value={formData.handicap}
              onChange={(e) => handleChange('handicap', e.target.value)}
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
              placeholder="18"
              min="0"
              max="54"
            />
            {errors.handicap && <p className="text-red-500 text-xs mt-1">{errors.handicap}</p>}
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-white mb-1">Address *</label>
            <GooglePlacesAutocomplete
              value={formData.location}
              onChange={(value) => handleChange('location', value)}
              onSelect={(locData) => setSelectedLocationData(locData)}
              placeholder="Start typing your address..."
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
            />
            <p className="text-xs text-gray-400 mt-1">The address is only visible to you. Users will only see the zip code. You must enter an address to find other golfers near you.</p>
            {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location}</p>}
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <p className="text-center text-xs text-gray-400 leading-relaxed">
            By creating an account with us you agree to the{' '}
            <a
              href="/terms"
              onClick={e => { e.preventDefault(); window.history.pushState({}, '', '/terms'); window.dispatchEvent(new PopStateEvent('popstate')); }}
              className="text-[#22c55e] hover:underline"
            >
              Terms of Service
            </a>
            {' '}and{' '}
            <a
              href="/privacypolicy"
              onClick={e => { e.preventDefault(); window.history.pushState({}, '', '/privacypolicy'); window.dispatchEvent(new PopStateEvent('popstate')); }}
              className="text-[#22c55e] hover:underline"
            >
              Privacy Policy
            </a>
            .
          </p>
          <p className="text-xs text-gray-400 text-center">
            This site is protected by reCAPTCHA and the Google{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-300">Privacy Policy</a>
            {' '}and{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-300">Terms of Service</a>
            {' '}apply.
          </p>
          {Object.keys(errors).length > 0 && (
            <p className="text-red-500 text-sm text-center">Please fix the errors in this form.</p>
          )}
          <button
            onClick={handleRegister}
            disabled={registerLoading}
            className="w-full bg-[#16a34a] text-pure-white py-3 rounded-lg font-medium hover:bg-[#15803d] disabled:opacity-50"
          >
            {registerLoading ? 'Creating Account...' : 'Create Account'}
          </button>
          <button
            onClick={onBackToLogin}
            className="w-full bg-gray-800 text-white py-3 rounded-lg font-medium hover:bg-gray-700"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegistrationScreen;
